<template>
  <div class="p-5 space-y-5 bg-gray-50/40">
    <section class="border border-gray-200 rounded-lg bg-white p-4">
      <div class="flex items-end gap-4 flex-wrap">
        <label class="text-xs text-gray-600">
          <span class="block mb-1">Period</span>
          <select v-model.number="filters.rangeDays" :class="SELECT_CLASS" @change="reloadData">
            <option :value="1">Last 24 hours</option>
            <option :value="7">Last 7 complete days</option>
            <option :value="30">Last 30 complete days</option>
          </select>
        </label>
        <label class="text-xs text-gray-600">
          <span class="block mb-1">Traffic</span>
          <select v-model="filters.trafficScope" :class="SELECT_CLASS" @change="trafficChanged">
            <option value="customer">Customers</option>
            <option value="internal">Kairon staff</option>
            <option value="all">All traffic</option>
          </select>
        </label>
        <label class="text-xs text-gray-600">
          <span class="block mb-1">Tenant</span>
          <select v-model="filters.tenant" :class="`${SELECT_CLASS} sensitive`" @change="reloadData">
            <option :value="null">All tenants</option>
            <option v-for="tenant in options.tenants" :key="tenant.value" :value="tenant.value">
              {{ tenant.value }} ({{ formatCount(tenant.calls) }})
            </option>
          </select>
        </label>
        <label class="text-xs text-gray-600">
          <span class="block mb-1">Method</span>
          <select v-model="filters.method" :class="SELECT_CLASS" @change="reloadData">
            <option :value="null">All methods</option>
            <option v-for="method in methods" :key="method" :value="method">{{ method }}</option>
          </select>
        </label>
        <label class="text-xs text-gray-600 min-w-56">
          <span class="block mb-1">Page</span>
          <select v-model="filters.pageRoute" :class="`${SELECT_CLASS} w-full`" @change="reloadData">
            <option :value="null">All pages</option>
            <option v-for="page in options.pages" :key="page.value" :value="page.value">
              {{ page.value }} ({{ formatCount(page.calls) }})
            </option>
          </select>
        </label>
        <label class="text-xs text-gray-600 min-w-56 flex-1">
          <span class="block mb-1">Endpoint</span>
          <input
            v-model="filters.endpointSearch"
            type="search"
            placeholder="Search normalized paths"
            class="h-8 w-full border border-gray-300 rounded px-3 text-xs text-gray-800"
            @input="scheduleEndpointReload"
            @keyup.enter="reloadData"
          />
        </label>
        <button type="button" class="text-xs text-primary-700 hover:underline mb-2" @click="resetFilters">
          Reset
        </button>
      </div>
      <p v-if="errors.options" class="mt-3 text-xs text-red-dark">{{ errors.options }}</p>
      <div class="flex items-center justify-between mt-3 text-[11px] text-gray-500">
        <span>Browser-observed HTTP duration; customer traffic excludes AppUser.is_staff.</span>
        <span v-if="lastUpdated">Updated {{ lastUpdated }}</span>
      </div>
    </section>

    <section class="border border-gray-200 rounded-lg bg-white min-h-32">
      <div v-if="loading.summary" class="h-32 flex items-center justify-center">
        <LoadingIndicator message="Loading latency summary..." />
      </div>
      <div v-else-if="errors.summary" class="p-5 text-sm text-red-dark">{{ errors.summary }}</div>
      <div v-else class="grid grid-cols-5 divide-x divide-gray-200">
        <div v-for="stat in summaryCards" :key="stat.label" class="p-5">
          <div class="text-[11px] uppercase tracking-wide text-gray-600 font-semibold">{{ stat.label }}</div>
          <div class="text-[22px] leading-none font-semibold text-gray-900 mt-2">{{ stat.value }}</div>
          <div v-if="stat.note" class="text-[11px] text-gray-500 mt-1">{{ stat.note }}</div>
          <div class="text-xs mt-2" :class="stat.deltaClass">{{ stat.delta }}</div>
        </div>
      </div>
    </section>

    <Latency_Trend
      :rows="summary.trend"
      :range-days="filters.rangeDays"
      :loading="loading.summary"
      :error="errors.summary"
    />

    <Latency_EndpointTable
      :rows="endpoints"
      :loading="loading.endpoints"
      :error="errors.endpoints"
    />

    <section class="border border-gray-200 rounded-lg bg-white px-5 py-3 flex items-center gap-3">
      <span class="text-xs font-medium text-gray-700">Long request threshold</span>
      <button
        v-for="threshold in thresholds"
        :key="threshold.value"
        type="button"
        class="h-8 px-3 rounded text-xs border"
        :class="filters.longThreshold === threshold.value
          ? 'border-primary-500 bg-primary-100 text-primary-700 font-semibold'
          : 'border-gray-200 text-gray-700 hover:bg-gray-50'"
        @click="setThreshold(threshold.value)"
      >{{ threshold.label }}</button>
    </section>

    <Latency_RequestTable
      :rows="longRequests"
      :loading="loading.longRequests"
      :error="errors.longRequests"
    />
  </div>
</template>

<script>
import axios from 'axios';
import abortable from '@/mixins/abortable';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Latency_EndpointTable from './Latency_EndpointTable.vue';
import Latency_RequestTable from './Latency_RequestTable.vue';
import Latency_Trend from './Latency_Trend.vue';
import {
  deltaClass, formatCount, formatDelta, formatDuration, formatRate, formatWait,
} from './latency_format';

const SELECT_CLASS = 'h-8 border border-gray-300 rounded px-3 text-xs text-gray-800 bg-white';

export default {
  name: 'SupportAppLatency',
  components: {
    Latency_EndpointTable, Latency_RequestTable, Latency_Trend, LoadingIndicator,
  },
  mixins: [abortable],
  data() {
    return {
      SELECT_CLASS,
      methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
      thresholds: [
        { value: 5000, label: '5 seconds' },
        { value: 10000, label: '10 seconds' },
        { value: 25000, label: '25 seconds' },
      ],
      filters: {
        rangeDays: 7,
        trafficScope: 'customer',
        tenant: null,
        method: null,
        pageRoute: null,
        endpointSearch: '',
        longThreshold: 10000,
      },
      options: { tenants: [], pages: [] },
      summary: { current: null, prior: null, trend: [] },
      endpoints: [],
      longRequests: [],
      loading: { summary: true, endpoints: true, longRequests: true, options: true },
      errors: { summary: null, endpoints: null, longRequests: null, options: null },
      lastUpdated: null,
      endpointTimer: null,
    };
  },
  computed: {
    summaryCards() {
      const current = this.summary.current;
      const prior = this.summary.prior;
      if (!current) return [];
      const over2 = this.rate(current, 'over_2s');
      const priorOver2 = this.rate(prior, 'over_2s');
      const over10 = this.rate(current, 'over_10s');
      const priorOver10 = this.rate(prior, 'over_10s');
      return [
        this.card('P90', formatDuration(current.p90_ms), current.p90_ms, prior?.p90_ms),
        this.card('P95', formatDuration(current.p95_ms), current.p95_ms, prior?.p95_ms),
        this.rateCard('Requests ≥2s', formatRate(over2), over2, priorOver2),
        this.rateCard(
          'Requests ≥10s', formatCount(current.over_10s), over10, priorOver10,
          `${formatRate(over10)} of ${formatCount(current.calls)} calls`,
        ),
        this.card(
          'Excess wait', formatWait(current.excess_wait_minutes),
          current.excess_wait_minutes, prior?.excess_wait_minutes,
        ),
      ];
    },
  },
  created() {
    this.fetchOptions();
    this.reloadData();
  },
  beforeUnmount() {
    if (this.endpointTimer) clearTimeout(this.endpointTimer);
  },
  methods: {
    formatCount,
    rate(row, key) {
      return row?.calls ? row[key] / row.calls : null;
    },
    card(label, value, current, prior, note = null) {
      const change = prior ? 100 * (current - prior) / prior : null;
      return {
        label, value, note,
        delta: prior ? `${formatDelta(change)} vs prior` : 'No prior comparison',
        deltaClass: deltaClass(change),
      };
    },
    rateCard(label, value, current, prior, note = null) {
      const points = current !== null && prior !== null ? 100 * (current - prior) : null;
      const delta = points === null
        ? 'No prior comparison'
        : `${points > 0 ? '↑' : points < 0 ? '↓' : '→'} ${Math.abs(points).toFixed(1)}pp vs prior`;
      return { label, value, note, delta, deltaClass: deltaClass(points) };
    },
    params(section) {
      return {
        section,
        range_days: this.filters.rangeDays,
        traffic_scope: this.filters.trafficScope,
        filter_tenant: this.filters.tenant,
        method: this.filters.method,
        page_route: this.filters.pageRoute,
        endpoint_search: this.filters.endpointSearch || null,
        long_threshold_ms: this.filters.longThreshold,
      };
    },
    async fetchSection(section, target, signal) {
      this.loading[target] = true;
      this.errors[target] = null;
      try {
        const { data } = await axios.get('/api/tracking/app-latency/', {
          params: this.params(section), signal,
        });
        if (target === 'summary') this.summary = data;
        else if (target === 'endpoints') this.endpoints = data.rows || [];
        else this.longRequests = data.rows || [];
      } catch (error) {
        if (this._isAborted(error)) return;
        this.errors[target] = error.response?.data?.detail || `Could not load ${target}.`;
      } finally {
        if (!signal.aborted) this.loading[target] = false;
      }
    },
    async reloadData() {
      const signals = {
        summary: this._renewAbortSignal('latency-summary'),
        endpoints: this._renewAbortSignal('latency-endpoints'),
        longRequests: this._renewAbortSignal('latency-long-requests'),
      };
      await Promise.all([
        this.fetchSection('summary', 'summary', signals.summary),
        this.fetchSection('endpoints', 'endpoints', signals.endpoints),
        this.fetchSection('long_requests', 'longRequests', signals.longRequests),
      ]);
      if (!Object.values(signals).some((signal) => signal.aborted)) {
        this.lastUpdated = new Date().toLocaleTimeString(undefined, {
          hour: 'numeric', minute: '2-digit',
        });
      }
    },
    async fetchOptions() {
      const signal = this._renewAbortSignal('latency-options');
      this.loading.options = true;
      this.errors.options = null;
      try {
        const { data } = await axios.get('/api/tracking/app-latency/', {
          params: this.params('filters'), signal,
        });
        this.options = data;
        if (this.filters.tenant
            && !data.tenants.some((item) => item.value === this.filters.tenant)) {
          this.filters.tenant = null;
        }
        if (this.filters.pageRoute
            && !data.pages.some((item) => item.value === this.filters.pageRoute)) {
          this.filters.pageRoute = null;
        }
      } catch (error) {
        if (this._isAborted(error)) return;
        this.errors.options = error.response?.data?.detail || 'Could not load filter options.';
      } finally {
        if (!signal.aborted) this.loading.options = false;
      }
    },
    trafficChanged() {
      this.filters.tenant = null;
      this.filters.pageRoute = null;
      this.fetchOptions();
      this.reloadData();
    },
    scheduleEndpointReload() {
      if (this.endpointTimer) clearTimeout(this.endpointTimer);
      this.endpointTimer = setTimeout(() => this.reloadData(), 400);
    },
    setThreshold(value) {
      this.filters.longThreshold = value;
      const signal = this._renewAbortSignal('latency-long-requests');
      this.fetchSection('long_requests', 'longRequests', signal);
    },
    resetFilters() {
      this.filters = {
        rangeDays: 7,
        trafficScope: 'customer',
        tenant: null,
        method: null,
        pageRoute: null,
        endpointSearch: '',
        longThreshold: 10000,
      };
      this.fetchOptions();
      this.reloadData();
    },
  },
};
</script>
